import React from 'react';

const AVG_MONTH_DAYS = 30.44;
const COUNT_KPIS = new Set([
  'fa_presented','fa_sold_total','fa_sold_same_day','fa_sold_previous',
  'arches_total','arches_same_day','arches_previous','consults_scheduled',
  'consults_showed','inbound_calls','total_revenue','collections_eod',
  'surgical_production','total_collected'
]);

function getProratedGoal(monthlyGoals, goalKey, periodDays) {
  if (!goalKey || !monthlyGoals) return null;
  const mg = monthlyGoals[goalKey];
  if (mg === null || mg === undefined || mg === '') return null;
  const n = Number(mg);
  if (isNaN(n) || n <= 0) return null;
  if (!COUNT_KPIS.has(goalKey) || !periodDays || periodDays <= 0) return n;
  return Math.round(n * periodDays / AVG_MONTH_DAYS);
}

// Clean SVG Funnel — no goal clutter inside, just values
const FunnelSVG = ({ data }) => {
  const W = 760;
  const LABEL_W = 130;
  const PCT_W = 60;
  const FUNNEL_X = LABEL_W;
  const FUNNEL_W = W - LABEL_W - PCT_W;
  const SEG_H = 56;
  const GAP = 5;
  const TOTAL_H = data.length * SEG_H + (data.length - 1) * GAP + 20;
  const maxVal = Math.max(1, data[0]?.value || 1);
  const MIN_RATIO = 0.08;

  const COLORS = [
    { top: '#3B82F6', bot: '#2563EB' },
    { top: '#2563EB', bot: '#1D4ED8' },
    { top: '#1D4ED8', bot: '#1E40AF' },
    { top: '#1E40AF', bot: '#1E3A8A' },
    { top: '#1E3A8A', bot: '#172554' },
  ];

  const segments = data.map((stage, i) => {
    const ratio = Math.max(MIN_RATIO, (stage.value || 0) / maxVal);
    const segW = ratio * FUNNEL_W;
    const segX = FUNNEL_X + (FUNNEL_W - segW) / 2;
    const y = i * (SEG_H + GAP);
    const nextRatio = i < data.length - 1 ? Math.max(MIN_RATIO, (data[i + 1]?.value || 0) / maxVal) : ratio;
    const nextW = nextRatio * FUNNEL_W;
    const nextX = FUNNEL_X + (FUNNEL_W - nextW) / 2;
    const convPct = i === 0 ? 100 : data[i - 1]?.value > 0 ? Math.round((stage.value || 0) / data[i - 1].value * 100) : 0;
    const path = [`M ${segX} ${y}`, `L ${segX + segW} ${y}`, `L ${nextX + nextW} ${y + SEG_H}`, `L ${nextX} ${y + SEG_H}`, 'Z'].join(' ');
    const color = COLORS[Math.min(i, COLORS.length - 1)];
    const midY = y + SEG_H / 2;
    const midX = FUNNEL_X + FUNNEL_W / 2;
    return { ...stage, y, midY, midX, segX, segW, path, color, convPct };
  });

  const convColor = (pct, i) => i === 0 ? '#94A3B8' : pct >= 50 ? '#16A34A' : pct >= 25 ? '#D97706' : '#DC2626';

  return (
    <svg viewBox={`0 0 ${W} ${TOTAL_H}`} preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        {segments.map((seg, i) => (
          <linearGradient key={i} id={`fg2-${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={seg.color.top} />
            <stop offset="100%" stopColor={seg.color.bot} />
          </linearGradient>
        ))}
      </defs>
      {segments.map((seg, i) => (
        <g key={i}>
          <path d={seg.path} fill={`url(#fg2-${i})`} />
          {i > 0 && <line x1={seg.segX} y1={seg.y} x2={seg.segX + seg.segW} y2={seg.y} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />}
          {/* Label left */}
          <text x={LABEL_W - 12} y={seg.midY + 5} textAnchor="end" fill="#475569" fontSize="11.5"
            fontFamily="Inter, sans-serif" fontWeight="600">
            {seg.label.toUpperCase()}
          </text>
          {/* Value centered */}
          <text x={seg.midX} y={seg.midY + 7} textAnchor="middle" fill="white" fontSize="20"
            fontFamily="Space Grotesk, sans-serif" fontWeight="700">
            {(seg.value || 0).toLocaleString()}
          </text>
          {/* Conversion right */}
          <text x={W - PCT_W + 8} y={seg.midY + 5} textAnchor="start"
            fill={convColor(seg.convPct, i)} fontSize="11.5" fontFamily="Inter, sans-serif" fontWeight="700">
            {i === 0 ? '100%' : `${seg.convPct}%`}
          </text>
          {i > 0 && <text x={W - PCT_W + 12} y={seg.y - 1} textAnchor="start" fill="#CBD5E1" fontSize="9" fontFamily="Inter, sans-serif">vs prev</text>}
        </g>
      ))}
      <text x={W - PCT_W + 8} y={12} textAnchor="start" fill="#94A3B8" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="600">CONV.</text>
      <text x={LABEL_W - 12} y={12} textAnchor="end" fill="#94A3B8" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="600">STAGE</text>
    </svg>
  );
};

// Goal tracker strip below the funnel
const GoalStrip = ({ data, monthlyGoals, periodDays }) => {
  const stagesWithGoals = data.map(stage => {
    const goal = getProratedGoal(monthlyGoals, stage.goalKey, periodDays);
    if (!goal || goal <= 0) return null;
    const actual = stage.value || 0;
    const pct = Math.round(actual / goal * 100);
    const meets = actual >= goal;
    return { label: stage.label, actual, goal, pct, meets };
  }).filter(Boolean);

  if (stagesWithGoals.length === 0) return null;

  return (
    <div className="px-6 pb-5 border-t border-slate-100 pt-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Goal Progress</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3">
        {stagesWithGoals.map(s => (
          <div key={s.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-600">{s.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 tabular-nums">
                  {s.actual.toLocaleString()} <span className="text-slate-300">/</span> {s.goal.toLocaleString()}
                </span>
                <span className={`text-xs font-bold tabular-nums ${
                  s.meets ? 'text-green-600' : s.pct >= 75 ? 'text-amber-600' : 'text-red-500'
                }`}>{s.pct}%</span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(100, s.pct)}%`,
                  background: s.meets
                    ? 'linear-gradient(90deg,#15803D,#22C55E)'
                    : s.pct >= 75
                      ? 'linear-gradient(90deg,#B45309,#F59E0B)'
                      : 'linear-gradient(90deg,#1D4ED8,#60A5FA)'
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function FunnelChart({ data, monthlyGoals, periodDays }) {
  if (!data || data.length === 0) return null;

  return (
    <div className="w-full bg-white" style={{ borderRadius: '0 0 12px 12px' }}>
      <FunnelSVG data={data} />
      <GoalStrip data={data} monthlyGoals={monthlyGoals} periodDays={periodDays} />
    </div>
  );
}
